import { IPlacement } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { Checkbox } from "components/ui/checkbox";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { FunctionComponent } from "react";
import { useNow } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import AdditionalClientInfo from "./additional-client-info";
import ClientInfoHeader from "./header";
import PrimaryClientInfo from "./primary-client-info";

type ClientInfoFormProps = {
    placement: IPlacement;
    onFieldChange: (fieldName: string, newValue: unknown) => void;
    onMultiFieldChange: (newValues: Record<string, unknown>) => void;
};

const CONFIRM_DATA_OVERWRITE_TEXT = `This will copy the Best Contact person's details.
    It will overwrite at least some of "Client's" details that
    you've already typed below (this may be exactly what you want
    to do!).`;

export const ClientInfoForm: FunctionComponent<ClientInfoFormProps> = props => {
    const { placement, onFieldChange, onMultiFieldChange } = props;
    const now = useNow();

    const { settings } = useTenantSettings();

    const sameAsBestContactCheckboxChanged = (newValue: boolean) => {
        if (!newValue) {
            onFieldChange("clientSameAsBestContact", newValue);
        } else {
            (async () => {
                const result = await fancyConfirm(
                    "Copy Best Contact Info?",
                    CONFIRM_DATA_OVERWRITE_TEXT,
                    "Yes, I'm sure",
                    "Cancel",
                );
                if (result) {
                    onMultiFieldChange({
                        name: placement.bestContactEntity.name,
                        cellPhone: placement.bestContactEntity.cellPhone,
                        phone: placement.bestContactEntity.phone1,
                        email: placement.bestContactEntity.email1,
                        emailOptOut: placement.bestContactEntity.email1OptOut,
                        address: placement.bestContactEntity.address1,
                        city: placement.bestContactEntity.city,
                        state: placement.bestContactEntity.state,
                        zip: placement.bestContactEntity.zip,
                        clientSameAsBestContact: true,
                    });
                }
            })();
        }
    };

    const onCreateNewAdditionalClient = () => onFieldChange("additionalClient", {});

    const onDeleteAdditionalClient = async () => {
        const result = await fancyConfirm(
            "Delete additional client?",
            "This will delete all the additional client data",
            "Yes, I'm sure",
            "Cancel",
        );
        if (result) {
            onFieldChange("additionalClient", null);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const onAdditionalClientFieldChange = (fieldName: string, newValue: any) => {
        onFieldChange(`additionalClient.${fieldName}`, newValue);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const onAdditionalClientMultiFieldChange = (newValues: any) => {
        const additionalClientNewValues = {};
        for (const [key, value] of Object.entries(newValues)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
            (additionalClientNewValues as any)[`additionalClient.${key}`] = value;
        }

        onMultiFieldChange(additionalClientNewValues);
    };

    const hasAdditionalClient = !!placement.additionalClient;

    const clientBasicInfo = {
        id: placement.id,
        name: placement.name,
        preferredName: placement.preferredName,
        email: placement.email,
        phone: placement.phone,
        cellPhone: placement.cellPhone,
        birthday: placement.birthday,
        isBirthdayFake: placement.isBirthdayFake,
        gender: placement.gender,
        weight: placement.weight,
        height: placement.height,
        maritalStatus: placement.maritalStatus,
    };

    const title = "Client" + (hasAdditionalClient ? "s" : "");

    return (
        <InputFormBlock>
            <ClientInfoHeader title={title} showIcon isCouple={hasAdditionalClient}>
                {!settings.showClientFirst && (
                    <div>
                        <Checkbox
                            onChange={e =>
                                sameAsBestContactCheckboxChanged(e.target.checked)
                            }
                            checked={placement.clientSameAsBestContact}
                            label="Resident is same as Best Contact"
                        />
                    </div>
                )}
            </ClientInfoHeader>
            <PrimaryClientInfo
                clientBasicInfo={clientBasicInfo}
                clientSameAsBestContact={placement.clientSameAsBestContact}
                onFieldChange={onFieldChange}
                onMultiFieldChange={onMultiFieldChange}
                showClientIntakeHeight={settings.showClientIntakeHeight}
                showClientIntakeWeight={settings.showClientIntakeWeight}
                now={now}
                emailOptOut={placement.emailOptOut}
                hasAdditionalClient={hasAdditionalClient}
            />
            <div className="mb-5" />
            {hasAdditionalClient ? (
                <AdditionalClientInfo
                    clientInfo={placement.additionalClient}
                    clientId={placement.id}
                    clientSameAsBestContact={placement.clientSameAsBestContact}
                    showClientIntakeWeight={settings.showClientIntakeWeight}
                    showClientIntakeHeight={settings.showClientIntakeHeight}
                    emailOptOut={placement.emailOptOut}
                    now={now}
                    onFieldChange={onAdditionalClientFieldChange}
                    onMultiFieldChange={onAdditionalClientMultiFieldChange}
                    onDeleteAdditionalClient={onDeleteAdditionalClient}
                />
            ) : (
                <SecondaryButton onClick={onCreateNewAdditionalClient}>
                    Add another client
                </SecondaryButton>
            )}
        </InputFormBlock>
    );
};
